import React from 'react';
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    ModalCloseButton,
    Text,
    Button,
    HStack,
    Box,
    UnorderedList,
    ListItem,
    Image,
} from '@chakra-ui/react';

const ACCENT = '#0071E3';
const ACCENT_HOVER = '#3544a0';

export interface InvoiceReprocessPagesConfirmModalProps {
    isOpen: boolean;
    onClose: () => void;
    isLoading: boolean;
    onConfirm: () => void;
    isPdf: boolean;
    pageCount: number;
    /** Data URLs or blob URLs for each page in upload order (optional). */
    previewUrls?: string[];
}

const InvoiceReprocessPagesConfirmModal = ({
    isOpen,
    onClose,
    isLoading,
    onConfirm,
    isPdf,
    pageCount,
    previewUrls = [],
}: InvoiceReprocessPagesConfirmModalProps) => {
    return (
        <Modal isOpen={isOpen} onClose={onClose} size="xl">
            <ModalOverlay />
            <ModalContent borderRadius="xl" overflow="hidden">
                <ModalHeader fontSize="lg" fontWeight="bold" color="gray.900">
                    Re-process invoice with new pages?
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody pb={4}>
                    <Box bg="orange.50" border="1px solid" borderColor="orange.200" borderRadius="md" p={3} mb={4}>
                        <Text fontSize="sm" color="orange.800" fontWeight="medium">
                            You are about to run OCR, extraction, and NEX reconciliation again using{' '}
                            {pageCount} {pageCount === 1 ? 'page' : 'pages'} from your {isPdf ? 'PDF' : 'images'}.
                            Line items, matches, and manual corrections from the current version will be replaced by
                            whatever the new pass produces.
                        </Text>
                    </Box>
                    {previewUrls.length > 0 && (
                        <Box mb={4}>
                            <Text fontSize="sm" fontWeight="semibold" color="gray.800" mb={2}>
                                Pages to upload (in order)
                            </Text>
                            <Box
                                border="1px solid"
                                borderColor="gray.200"
                                borderRadius="md"
                                p={2}
                                bg="gray.50"
                                maxH="200px"
                                overflowX="auto"
                                overflowY="hidden"
                            >
                                <HStack spacing={2} align="stretch" flexWrap="nowrap">
                                    {previewUrls.map((url, idx) => (
                                        <Box
                                            key={`reprocess-preview-${idx}`}
                                            flexShrink={0}
                                            w="72px"
                                            h="56px"
                                            borderRadius="md"
                                            overflow="hidden"
                                            border="1px solid"
                                            borderColor="gray.300"
                                            bg="white"
                                        >
                                            <Image
                                                src={url}
                                                alt={`Page ${idx + 1}`}
                                                w="100%"
                                                h="100%"
                                                objectFit="cover"
                                            />
                                        </Box>
                                    ))}
                                </HStack>
                            </Box>
                        </Box>
                    )}
                    <Text fontSize="sm" color="gray.700" mb={2}>
                        This is intended when scans were missing, duplicated, or in the wrong order. After processing
                        finishes, review the invoice again before submitting to NEX.
                    </Text>
                    <UnorderedList fontSize="sm" color="gray.600" spacing={1}>
                        <ListItem>Your edits and reconciliation choices from this version will not carry over.</ListItem>
                        <ListItem>Nothing is saved from this re-process until you confirm below and the server finishes successfully.</ListItem>
                        <ListItem>If processing fails, the previous stored invoice remains unchanged.</ListItem>
                    </UnorderedList>
                </ModalBody>
                <ModalFooter>
                    <HStack spacing={3}>
                        <Button size="action" variant="outlineGray" onClick={onClose} isDisabled={isLoading}>
                            Cancel
                        </Button>
                        <Button
                            size="action"
                            color="white"
                            bg={ACCENT}
                            _hover={{ bg: ACCENT_HOVER }}
                            onClick={onConfirm}
                            isLoading={isLoading}
                            loadingText="Uploading…"
                        >
                            Re-process now
                        </Button>
                    </HStack>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default InvoiceReprocessPagesConfirmModal;
